import { Controller } from '@hotwired/stimulus'

import { Chart } from 'chart.js'
import { CrosshairPlugin } from 'chartjs-plugin-crosshair'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { ChoroplethController, GeoFeature, ProjectionScale, ColorScale } from 'chartjs-chart-geo'
import { mergeDeepLeft } from 'ramda'

import { buildCallsReceivablesAndPayablesCombo } from '../charts/calls_receivables_and_payables_combo'
import { applyGlobalChartDefaults } from '../charts/theme'
import {
    chartPalette,
    cssVar,
    verticalGradient,
    choroplethInterpolate,
    BAR_CAP_RADIUS,
} from '../charts/palette'
import { formatNumber } from '../app/modules/utilities'

Chart.register(ChoroplethController, GeoFeature, ProjectionScale, ColorScale)

// Skip drawing until the labels have been prepared, to avoid Turbo frame
// teardown/rebuild issues.
const SafeChartDataLabels = {
    ...ChartDataLabels,
    afterDatasetsDraw(chart, args, options) {
        if (!chart['$datalabels']?._labels) return
        return ChartDataLabels.afterDatasetsDraw(chart, args, options)
    },
}

// Renderer registry: a presenter can return a `renderer` key (alongside the usual
// type/data) to delegate canvas construction to a bespoke build module instead of the
// default JSON-merge path. Charts with no `renderer` key are unaffected.
const CUSTOM_RENDERERS = {
    calls_receivables_and_payables_combo: buildCallsReceivablesAndPayablesCombo,
    // Legacy key still emitted by browser tabs loaded before this rename deployed. Keep it
    // registered until those drain, then drop it (RET-4289 follow-up).
    calls_conversions_combo: buildCallsReceivablesAndPayablesCombo,
}

export default class extends Controller {
    static targets = [
        'canvas',
        'options',
        'chartContainer',
        'toolbar',
        'legend',
        'comboModeButton',
        'comboSwitch',
    ]

    // Header toggles drive the combo renderer client-side. State is NOT persisted; it lives
    // on the controller (which is on the .card, so it survives turbo-frame data reloads of
    // the body). Defaults mirror the initial header markup (Counts active, Receivables % off).
    comboState = { mode: 'counts', showReceivables: false }

    optionsTargetConnected() {
        this.chart ? this.refreshChart() : this.initChart()
    }

    initChart() {
        // Apply the global Chart.js theme once, lazily: by the first chart's connect every
        // Chart.js component is registered (charts_js_action_controller imports chart.js/auto),
        // so the tooltip/plugin defaults this themes actually exist. Idempotent (see theme.js).
        applyGlobalChartDefaults()

        // A presenter can register a custom renderer (e.g. the calls combo). If one handles
        // this chart, keep only the resize wiring the default path sets up.
        if (this.#buildComboChart()) {
            this._resizeChartHandler = () => this.resizeChart()
            window.addEventListener('resize', this._resizeChartHandler)
            return
        }

        this.optionsFromParam.plugins = []

        const opts = mergeDeepLeft(this.optionsFromParam, {
            options: this.defaultRetreaverChartOptions,
            plugins: [],
        })

        opts.data.labels = this.truncateLabels(this.optionsFromParam.data.labels)
        this.#resolveCssVarColors(opts.data)
        this.#applyLineAreaGradients(opts.data, opts.type)

        if (this.isEmpty) {
            opts.data = this.emptyData
        }

        if (this.enabledPlugins.includes('datalabels')) {
            opts.plugins.push(SafeChartDataLabels) // Adding js
        }

        if (opts.type === 'line') {
            opts.plugins.push(CrosshairPlugin)
        }

        // Render the legend as HTML in the card footer instead of on the canvas.
        opts.plugins.push(this.htmlLegendPlugin)

        this.chart = new Chart(this.canvasTarget, opts)
        this.#setChartContainerHeightByLabelHeight()

        // Resize chart on window resize
        this.resizeChart()
        this._resizeChartHandler = () => this.resizeChart()
        window.addEventListener('resize', this._resizeChartHandler)
    }

    disconnect() {
        window.removeEventListener('resize', this._resizeChartHandler)
        this.chart?.destroy()
        this.chart = null
    }

    refreshChart() {
        // Custom-renderer charts (e.g. the combo) bind gradients/axes to the live canvas,
        // so a data reload destroys and rebuilds rather than patching the dataset arrays.
        if (this.#buildComboChart()) return

        this.chart.data = this.optionsFromParam.data
        this.chart.data.labels = this.truncateLabels(this.optionsFromParam.data.labels)
        this.#resolveCssVarColors(this.chart.data)
        this.#applyLineAreaGradients(this.chart.data, this.chart.config.type)

        if (this.isEmpty) {
            this.chart.data = this.emptyData
        }

        this.chart.update('none')
        this.#setChartContainerHeightByLabelHeight()
        this.resizeChart()
    }

    resizeChart() {
        if (!this.hasChartContainerTarget || !this.hasToolbarTarget) return

        // During a Turbo teardown the container is detached from the document while the
        // chart is briefly still alive. Writing maxHeight below would nudge Chart.js'
        // ResizeObserver into resizing a parent-less canvas and crash. Bail out instead.
        // (Note: this runs from the htmlLegend afterUpdate hook during `new Chart(...)`,
        // before this.chart is assigned, so we can't guard on this.chart here.)
        if (!this.chartContainerTarget.isConnected) return

        const parentCard = this.chartContainerTarget.closest('.card')
        if (!parentCard) return
        const parentCardBody = parentCard.querySelector('.card-body')
        if (!parentCardBody) return

        // Find card and toolbar height
        const cardHeight = parentCard.clientHeight
        const toolbarHeight = this.toolbarTarget.clientHeight

        // The HTML legend lives in the card footer, which also eats vertical space.
        const footer = parentCard.querySelector('.card-footer')
        const footerHeight = footer ? footer.clientHeight : 0

        // Find card body padding
        const styles = getComputedStyle(parentCardBody)
        const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)

        // Calculate the max height of the chart container
        // [height of card] - [toolbar height] - [footer height] - [padding] = [chart viewport]
        const viewport = cardHeight - toolbarHeight - footerHeight - padding

        // Chart.js sizes the canvas to the container's max-height (it reads max-height but
        // ignores min-height — see getContainerSize in chart.js). A horizontal "ranked
        // breakdown" chart intentionally grows past the viewport (#labelDrivenHeight,
        // ~25px/label, card body scrolls), so the cap has to rise to meet that taller
        // content. Capping at the viewport would clip the canvas and squash the bars even
        // though min-height already made the container box tall.
        const maxHeight = Math.max(viewport, this.#labelDrivenHeight())
        const next = `${maxHeight}px`
        // Avoid re-writing the same value: the htmlLegend plugin calls this from
        // afterUpdate, and a needless style change can re-trigger a resize loop.
        if (this.chartContainerTarget.style.maxHeight !== next) {
            this.chartContainerTarget.style.maxHeight = next
        }
    }

    // ---- Header controls (combo renderer) -------------------------------------
    // Wired from chart_configurations/header_controls/<type> via data-action. The controls
    // live in the .card-header and this controller owns the .card, so it reaches them with
    // plain queries — no cross-controller plumbing.

    setComboMode(event) {
        const mode = event.params.mode
        if (this.comboState.mode === mode) return
        this.comboState.mode = mode
        this.#buildComboChart()
    }

    toggleReceivables(event) {
        this.comboState.showReceivables = event.target.checked
        this.#buildComboChart()
    }

    // Build (or rebuild) a custom-renderer chart (e.g. the calls combo) with the current
    // theme + toggle state, then sync the header controls. Returns false for charts without
    // a registered renderer, so callers fall through to the default Chart.js path. The combo
    // legend renders through the SAME htmlLegend plugin every chart uses (passed in here).
    #buildComboChart() {
        const renderer = CUSTOM_RENDERERS[this.optionsFromParam.renderer]
        if (!renderer) return false

        this.chart?.destroy()
        this.chart = renderer(this.canvasTarget, this.optionsFromParam, {
            ...this.comboState,
            plugins: [this.htmlLegendPlugin],
        })
        this.#syncComboControls()
        this.resizeChart()
        return true
    }

    // Reflect comboState into the header markup: the active mode button and the Receivables %
    // switch. No-ops when these targets are absent (every non-combo chart). The granularity
    // caption now renders in the footer legend (presenter `legendRightLabel`), and chip
    // hidden-state is owned by htmlLegend, so this never touches the footer legend.
    #syncComboControls() {
        this.comboModeButtonTargets.forEach((btn) =>
            btn.classList.toggle('active', btn.dataset.mode === this.comboState.mode),
        )

        if (this.hasComboSwitchTarget) {
            this.comboSwitchTarget.checked = this.comboState.showReceivables
        }
    }

    truncateLabels(labels) {
        this.originalLabelsByIndex = []
        let truncateLabels = []
        if (labels) {
            truncateLabels = [...labels]
            truncateLabels = truncateLabels.map((label, index) => {
                const truncateCount = 25
                let truncatedLabel = label
                if (label.length > truncateCount) {
                    truncatedLabel = `${label.substring(0, truncateCount)}...`
                }
                this.originalLabelsByIndex[index] = label
                return truncatedLabel
            })
        }

        return truncateLabels
    }

    // Resolve `var(--chart-*)` color tokens in dataset colors against the active theme.
    // The canvas can't read CSS custom properties (and the Rails backend can't read the
    // browser's theme), so a dataset names the token — e.g. backgroundColor: 'var(--chart-green)'
    // — and we swap in the resolved value here, keeping one source of truth (charts.scss) and
    // letting the color flip with color mode.
    #resolveCssVarColors(data) {
        if (!data || !data.datasets) return
        const resolve = (value) => {
            if (Array.isArray(value)) return value.map(resolve)
            if (typeof value !== 'string') return value
            const match = value.match(/^var\((--[\w-]+)\)$/)
            return match ? cssVar(match[1], value) : value
        }
        data.datasets.forEach((dataset) => {
            dataset.backgroundColor = resolve(dataset.backgroundColor)
            dataset.borderColor = resolve(dataset.borderColor)
        })
    }

    // Line datasets that opt into `fill` get the combo chart's cyan area-fill look: a
    // top→bottom gradient (verticalGradient helper) under a calls-line stroke. Colors come
    // from the palette so the fill tracks the active theme (the CSS vars flip in dark mode).
    // Only unstyled series are themed — an explicit color in the dataset still wins.
    #applyLineAreaGradients(data, type) {
        if (type !== 'line' || !data || !data.datasets) return
        const palette = chartPalette()
        data.datasets.forEach((dataset) => {
            if (!dataset.fill) return
            if (dataset.backgroundColor == null) {
                dataset.backgroundColor = verticalGradient([
                    [0, palette.areaFillTop],
                    [1, palette.areaFillBottom],
                ])
            }
            if (dataset.borderColor == null) dataset.borderColor = palette.callsLine
        })
    }

    // Horizontal charts ("ranked breakdown" bars) might have a ton of labels, so expand the
    // height of the chart to ~25px per label. Otherwise it fills the container height.
    #setChartContainerHeightByLabelHeight() {
        const height = this.#labelDrivenHeight()
        if (height) {
            this.chartContainerTarget.style.minHeight = `${height}px`
        }
    }

    // The on-canvas height a horizontal "ranked breakdown" chart needs: ~25px per label so
    // each row stays legible. Returns 0 for vertical charts, or when few enough labels fit
    // the card body (<= 24rem) and the chart should just fill it; above that the body scrolls.
    // Shared by #setChartContainerHeightByLabelHeight (min-height) and resizeChart (max-height)
    // so the two never disagree — Chart.js clips the canvas to max-height, so a cap below this
    // would squash the bars.
    #labelDrivenHeight() {
        // resizeChart runs from the htmlLegend afterUpdate hook, which fires during
        // `new Chart(...)` before this.chart is assigned — fall back to the param options.
        const options = this.chart?.options ?? this.optionsFromParam.options ?? {}
        const labels = this.chart?.data?.labels ?? this.optionsFromParam.data.labels ?? []
        if (options.indexAxis !== 'y') return 0
        const height = labels.length * 25 // 25px is roughly the height of a chartjs label
        const fitsContainer = 387 // If label sizes fit within 24rem it fills the full container height
        return height > fitsContainer ? height : 0
    }

    get isEmpty() {
        // if there is no dataset -> true
        // if there is no data in all datasets -> true
        // if there are only 0 in all data in all datasets -> true
        if (this.optionsFromParam.type != 'choropleth') {
            return (
                !this.optionsFromParam.data.datasets ||
                this.optionsFromParam.data.datasets.every(
                    (dataset) => !dataset.data || dataset.data.every((x) => x == 0),
                )
            )
        }
        return false
    }

    get emptyData() {
        const palette = chartPalette()
        const emptyData = {
            labels: ['No Data'],
            datasets: [
                {
                    label: 'No Data',
                    // For pie and doughnut we want a round circle and that is why the data is [1]
                    data: ['pie', 'doughnut'].includes(this.optionsFromParam.type) ? [1] : [],
                    backgroundColor: [palette.emptyFill], // Neutral color for empty state
                    borderColor: [palette.emptyBorder], // Border for empty state
                    borderWidth: 1,
                },
            ],
        }

        return emptyData
    }

    get optionsFromParam() {
        return JSON.parse(this.optionsTarget.textContent)
    }
    get enabledPlugins() {
        return JSON.parse(this.optionsTarget.textContent).plugins || []
    }
    get shouldSortData() {
        return this.optionsFromParam.shouldSortData || false
    }

    get defaultRetreaverChartOptions() {
        const palette = chartPalette()
        const options = {
            maintainAspectRatio: false,
            responsive: true,
            resizeDelay: 50,
            scales: {
                x: {
                    grid: {
                        color: palette.grid,
                        lineWidth: 1,
                    },
                },
                y: {
                    grid: {
                        color: palette.grid,
                        lineWidth: 1,
                    },
                },
            },
            animation: {
                onComplete: (chart) => {
                    if (!this.chartRendered) {
                        // Broadcast rendered event, useful for cross-controller coordination
                        this.dispatch('rendered', { detail: { chart } })
                        this.chartRendered = true
                    }
                },
            },
            plugins: {
                // The canvas legend is disabled; the htmlLegend plugin renders the
                // legend into the card footer instead (see #renderHtmlLegend). We keep
                // the options object so generateLabels still resolves for the HTML legend.
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        beforeLabel: (tooltipItem) => {
                            // data can come in three different ways raw.value, raw.x, raw
                            // The way what we need to provide this in order to work is:
                            // datasets: [ { label: "...", data: ...., formatter: 'dollar'} ]

                            const { formatter } = tooltipItem.dataset
                            const { raw } = tooltipItem
                            const selectedValue =
                                raw && typeof raw === 'object'
                                    ? (raw.value ?? raw.x ?? raw.y ?? raw)
                                    : raw
                            const numericValue =
                                typeof selectedValue === 'number'
                                    ? selectedValue
                                    : Number(selectedValue)

                            tooltipItem.formattedValue = formatNumber(numericValue, formatter)
                        },
                        title: (context) => {
                            const index = context[0]?.dataIndex
                            return this.originalLabelsByIndex[index] || context[0].label
                        },
                    },
                },
            },
            pointStyle: false,
            cubicInterpolationMode: 'monotone',
            tension: 0.4,
        }

        if (this.optionsFromParam.type == 'line') {
            // This is for the plugins crosshair
            options.plugins.crosshair = {
                line: {
                    color: palette.crosshair,
                    width: 1,
                },
                sync: {
                    enabled: false,
                },
                zoom: {
                    enabled: false,
                },
            }
        }

        if (['choropleth'].includes(this.optionsFromParam.type)) {
            options.plugins.legend.display = false
            options.plugins.tooltip.callbacks.title = (context) =>
                // NOTE: The tooltip title should be the state name -> Texas.
                context[0]?.raw?.feature?.properties?.name || ''

            options.scales = {
                projection: {
                    axis: 'x',
                    projection: 'albersUsa',
                },
                color: {
                    axis: 'x',
                    // Themed cyan ramp; zero/low fades to transparent so the card background
                    // (which flips with color mode) shows through — see choroplethInterpolate.
                    interpolate: choroplethInterpolate(palette),
                    // No-data states (value: nil) stay transparent rather than a hard white,
                    // so they read as the themed background like every other empty state.
                    missing: 'transparent',
                    // The color scale legend is rendered as a horizontal gradient in the
                    // card footer (#renderColorScaleLegend) instead of on the canvas.
                    display: false,
                },
            }

            // chartjs-chart-geo only drives the fill (backgroundColor) off the color scale;
            // the feature border otherwise falls through to the faint global grid color, so
            // empty states nearly vanish. Give the outline a dedicated themed color/width so
            // the map's shape reads even where the fill is transparent (matches the mockup).
            options.elements = {
                geoFeature: {
                    borderColor: palette.choroplethBorder,
                    borderWidth: 0.5,
                },
            }
        }

        // Round a BAR_CAP_RADIUS cap on the growing END of every bar — the top for vertical
        // columns, the right for horizontal rows. For STACKED bars only the last visible segment
        // of a row/column is capped, so the stack reads as one continuous bar with square inner
        // joins; GROUPED bars each cap their own end. Assumes non-negative values (true for all
        // our bar charts), so the cap is always at top/right.
        if (this.optionsFromParam.type === 'bar') {
            const horizontal = this.optionsFromParam.options?.indexAxis === 'y'
            const cap = horizontal
                ? { topRight: BAR_CAP_RADIUS, bottomRight: BAR_CAP_RADIUS }
                : { topLeft: BAR_CAP_RADIUS, topRight: BAR_CAP_RADIUS }
            const valueAxis = horizontal ? 'x' : 'y'
            const stacked = !!this.optionsFromParam.options?.scales?.[valueAxis]?.stacked

            options.elements = {
                bar: {
                    borderRadius: (ctx) => {
                        if (!stacked) return cap
                        const { chart, dataset, datasetIndex } = ctx
                        let lastVisible = 0
                        chart.data.datasets.forEach((ds, i) => {
                            if (chart.isDatasetVisible(i) && ds.stack === dataset.stack) {
                                lastVisible = i
                            }
                        })
                        return datasetIndex === lastVisible ? cap : 0
                    },
                },
            }
        }

        if (this.enabledPlugins.includes('datalabels')) {
            options.layout = { padding: { right: 40 } }

            options.plugins.datalabels = {
                padding: 0,
                font: { weight: 600, size: 11 },

                // put it at the very end of the stacked row
                anchor: 'end',
                align: 'end',
                offset: 4,
                clamp: true,
                clip: false,
                display: (ctx) => {
                    // only on the last *visible* dataset of this stack
                    const { chart, dataset } = ctx
                    let lastVisible = 1
                    chart.data.datasets.forEach((ds, i) => {
                        if (chart.isDatasetVisible(i) && ds.stack === dataset.stack) lastVisible = i
                    })
                    return ctx.datasetIndex === lastVisible
                },
                formatter: (value, ctx) => {
                    // sum only visible datasets in this stack for this row
                    const { chart, dataset, dataIndex } = ctx

                    let sum = 0
                    chart.data.datasets.forEach((ds, i) => {
                        if (chart.isDatasetVisible(i) && ds.stack === dataset.stack) {
                            const v = ds.data[dataIndex] ?? 0
                            sum += typeof v === 'object' ? (v.x ?? v.y ?? 0) : v
                        }
                    })

                    // We need the same formatter for all datasets in order to format the summed datalabel
                    let formatter
                    const formatters = [...new Set(chart.data.datasets.map((ds) => ds.formatter))]
                    if (formatters.length > 1) {
                        console.error(
                            'There are more than one formatter and cannot apply format: ',
                            formatters,
                        )
                    } else {
                        formatter = formatters[0]
                    }

                    return formatNumber(sum, formatter)
                },
            }

            if (['pie', 'doughnut'].includes(this.optionsFromParam.type)) {
                // # Easier to override the above config
                options.plugins.datalabels.anchor = null
                options.plugins.datalabels.color = 'white'
                options.plugins.datalabels.display = 'auto'
                options.plugins.datalabels.backgroundColor = (context) =>
                    context.dataset.backgroundColor
            }
        }

        if (['pie', 'doughnut'].includes(this.optionsFromParam.type)) {
            options.plugins.tooltip = {
                // This is for showing how many % is the piece of the pie.
                callbacks: {
                    label(context) {
                        // Some slices might be toggled off via the HTML legend, so the
                        // percentage is computed against only the visible slices. We read
                        // visibility from chart.getDataVisibility — the canvas legend is
                        // disabled (display:false) so chart.legend can't be relied on.
                        const { chart } = context
                        let total = 0
                        context.dataset.data.forEach((value, index) => {
                            if (chart.getDataVisibility(index)) {
                                total += value
                            }
                        })

                        // Fallback if every slice is hidden, to avoid dividing by zero.
                        if (total === 0) {
                            total = context.dataset.data.reduce((sum, value) => sum + value, 0)
                        }

                        const value = context.raw
                        const percentage = ((value / total) * 100).toFixed(2)
                        const { tooltipValues } = context.chart.config.data
                        const tooltipValue =
                            (tooltipValues && tooltipValues[context.dataIndex]) ?? value
                        return `${tooltipValue} (${percentage}%)`
                    },
                },
            }
        }

        return options
    }

    // Inline Chart.js plugin that renders the legend into the card footer as HTML,
    // mirroring the official htmlLegend sample. Scoped per controller instance so the
    // many charts on a dashboard each target their own footer (this.legendTarget)
    // rather than a shared global element id.
    get htmlLegendPlugin() {
        return {
            id: 'htmlLegend',
            // afterUpdate fires on every chart update (initial render, refresh, toggle).
            afterUpdate: (chart) => this.#renderHtmlLegend(chart),
        }
    }

    #renderHtmlLegend(chart) {
        if (!this.hasLegendTarget) return
        // afterUpdate can fire while the `options` <script> (lazy-loaded in the inner
        // turbo frame) is mid-swap and momentarily detached. isEmpty/optionsFromParam
        // read from it, so bail rather than throw "Missing target element options".
        if (!this.hasOptionsTarget) return

        const container = this.legendTarget

        // The choropleth has a continuous color scale rather than discrete series, so it
        // gets its own horizontal gradient legend.
        if (chart.config.type === 'choropleth') {
            this.#renderColorScaleLegend(chart, container)
            this.resizeChart()
            return
        }

        if (this.isEmpty) {
            // No legend for the empty "No Data" placeholder.
            container.classList.add('d-none')
            container.replaceChildren()
        } else {
            // Reuse Chart.js' built-in label generator — it still resolves even though
            // the canvas legend is disabled.
            const items = chart.options.plugins.legend.labels.generateLabels(chart)

            let list = container.querySelector('ul')
            if (!list) {
                list = document.createElement('ul')
                list.className =
                    'chart-legend list-unstyled d-flex flex-wrap m-0 row-gap-1 column-gap-3'
                container.appendChild(list)
            }
            list.replaceChildren()

            items.forEach((item) => {
                const li = document.createElement('li')
                li.className = 'chart-legend-item d-flex align-items-center gap-1 small lh-sm'
                // is-hidden is the test/state hook; opacity-50 dims the toggled-off chip.
                if (item.hidden) li.classList.add('is-hidden', 'opacity-50')
                li.addEventListener('click', () => this.#onLegendItemClick(item))

                // Swatch: line series read as a stroke (dashed when the dataset is) with a
                // centre dot; bar/pie series read as a filled box. The dataset's type drives
                // the choice, so this works for any chart — including mixed bar+line combos.
                const dataset =
                    item.datasetIndex != null ? chart.data.datasets[item.datasetIndex] : null
                const isLine = (dataset?.type || chart.config.type) === 'line'

                const swatch = document.createElement('span')
                swatch.className = 'chart-legend-swatch flex-shrink-0'
                if (isLine) {
                    swatch.classList.add('chart-legend-swatch-line')
                    if (Array.isArray(dataset?.borderDash) && dataset.borderDash.length) {
                        swatch.classList.add('is-dashed')
                    }
                    // The line/dot are CSS pseudo-elements coloured via this custom property.
                    swatch.style.setProperty('--chart-legend-color', item.strokeStyle)
                } else {
                    swatch.classList.add('rounded-1')
                    // Bars/pies: fillStyle can be a CanvasGradient (not a CSS string) — fall back
                    // to the series' solid stroke color so the swatch stays filled.
                    swatch.style.background =
                        typeof item.fillStyle === 'string' ? item.fillStyle : item.strokeStyle
                    swatch.style.borderColor = item.strokeStyle
                }
                li.appendChild(swatch)

                const label = document.createElement('span')
                label.className = 'chart-legend-label'
                if (item.hidden) label.classList.add('text-decoration-line-through')
                label.textContent = item.text
                li.appendChild(label)

                list.appendChild(li)
            })

            this.#renderLegendRightLabel(list)
            container.classList.remove('d-none')
        }

        // The footer's presence/height changes the room left for the canvas.
        this.resizeChart()
    }

    // Append the presenter's `legendRightLabel` (if any) to the far right of the legend row —
    // generic, additive metadata any chart can set (e.g. the combo's granularity caption).
    // ms-auto pushes it to the opposite end of the chips; absent label → nothing rendered.
    #renderLegendRightLabel(list) {
        const text = this.optionsFromParam.legendRightLabel
        if (!text) return
        const li = document.createElement('li')
        li.className = 'chart-legend-right ms-auto text-nowrap'
        li.textContent = text
        list.appendChild(li)
    }

    // Builds the choropleth's color scale as a horizontal gradient bar with min/max
    // labels, mirroring (in HTML) the on-canvas geo legend we disabled. Colors come
    // straight from the chartjs-chart-geo color scale so they match the map exactly.
    #renderColorScaleLegend(chart, container) {
        const scale = chart.scales.color
        const min = scale && scale.min
        const max = scale && scale.max

        // No data, or every state shares one value — nothing meaningful to show.
        if (!scale || !Number.isFinite(min) || !Number.isFinite(max) || min === max) {
            container.classList.add('d-none')
            container.replaceChildren()
            return
        }

        // Sample the scale across [0, 1] (normalized value space) into a CSS gradient.
        const steps = 16
        const stops = []
        for (let i = 0; i <= steps; i++) {
            stops.push(scale.getColor(i / steps))
        }
        const gradient = `linear-gradient(to right, ${stops.join(', ')})`

        const { formatter, label } = chart.data.datasets[0] || {}

        const wrap = document.createElement('div')
        wrap.className = 'chart-color-legend hstack gap-2 w-100 small'

        if (label) {
            const title = document.createElement('span')
            title.className = 'chart-color-legend-title flex-shrink-0 fw-medium'
            title.textContent = label
            wrap.appendChild(title)
        }

        const minLabel = document.createElement('span')
        minLabel.className =
            'chart-color-legend-label flex-shrink-0 text-body-secondary font-monospace'
        minLabel.textContent = formatNumber(min, formatter)
        wrap.appendChild(minLabel)

        const bar = document.createElement('span')
        bar.className = 'chart-color-legend-bar flex-fill rounded-1'
        bar.style.background = gradient
        wrap.appendChild(bar)

        const maxLabel = document.createElement('span')
        maxLabel.className =
            'chart-color-legend-label flex-shrink-0 text-body-secondary font-monospace'
        maxLabel.textContent = formatNumber(max, formatter)
        wrap.appendChild(maxLabel)

        container.replaceChildren(wrap)
        container.classList.remove('d-none')
    }

    #onLegendItemClick(item) {
        const { type } = this.chart.config

        if (['pie', 'doughnut'].includes(type)) {
            // Pie/doughnut have a single dataset; visibility is per slice.
            this.chart.toggleDataVisibility(item.index)
            this.chart.update()
            return
        }

        // bar/line: visibility is per dataset.
        this.chart.setDatasetVisibility(
            item.datasetIndex,
            !this.chart.isDatasetVisible(item.datasetIndex),
        )
        this.chart.update()

        // Ranked breakdown bars re-sort after a toggle. The 550ms delay is a stylistic
        // choice so you see the bar disappear and then the rows re-arrange.
        if (this.shouldSortData) {
            setTimeout(() => {
                this.#sortAndReorderData()
                this.chart.update()
            }, 550)
        }
    }

    #sortAndReorderData() {
        if (!this.shouldSortData) {
            return
        }

        // Computing the order of the indexes of all visible data
        const labelsLength = this.chart.data.labels.length
        const keys = new Array(labelsLength).fill(0)
        this.chart.data.datasets.forEach((_, idx) => {
            if (!this.chart.isDatasetVisible(idx)) {
                return
            }
            for (let i = 0; i < labelsLength; i++) {
                keys[i] += +this.optionsFromParam.data.datasets[idx].data[i] || 0
            }
        })
        const order = [...keys.keys()].sort((a, b) => keys[b] - keys[a])

        // Reordering Labels while keeping the full label reference list in sync
        const reorderedOriginalLabels = order.map((i) => this.optionsFromParam.data.labels[i])
        this.originalLabelsByIndex = reorderedOriginalLabels
        this.chart.data.labels = reorderedOriginalLabels.map((label) => this.#truncateLabel(label))

        // Reordering data inside each dataset
        this.chart.data.datasets.forEach((ds, idx) => {
            ds.data = order.map((i) => this.optionsFromParam.data.datasets[idx].data[i])
        })
    }

    #truncateLabel(label) {
        const truncateCount = 25
        if (typeof label !== 'string') {
            return label
        }
        return label.length > truncateCount ? `${label.substring(0, truncateCount)}...` : label
    }
}
